import React from 'react';
import _ from 'lodash';

export type EmailDisplayInput = string | readonly string[] | null | undefined;

/**
 * Coerces API values that may be a single email, a comma-separated string, or a list into a
 * trimmed, non-empty list of addresses.
 */
export function normalizeEmailList(value: EmailDisplayInput): string[] {
  if (value === null || value === '' || _.isEmpty(value)) {
    return [];
  }
  const entries = Array.isArray(value) ? [...value] : [value];
  if (_.isEmpty(entries)) {
    return [];
  }
  return entries
    .flatMap((entry: string) => entry.split(',').map((part: string) => part.trim()))
    .filter(Boolean);
}

/**
 * Renders one email with explicit break opportunities after @ and after each dot between
 * segments, so constrained layouts wrap at natural boundaries instead of mid-token.
 */
export function formatEmailWithBreakOpportunities(email: string): React.ReactNode {
  if (!email) {
    return '';
  }
  const at = email.indexOf('@');
  if (at === -1) {
    return joinSegmentsWithWbr(email, '.');
  }
  const local = email.slice(0, at);
  const domain = email.slice(at + 1);

  return (
    <>
      {joinSegmentsWithWbr(local, '.')}
      @
      <wbr />
      {joinSegmentsWithWbr(domain, '.')}
    </>
  );
}

/**
 * One React fragment tree per email (same order as {@link normalizeEmailList}).
 */
export function formatEmailsToDisplayFragments(value: EmailDisplayInput): React.ReactNode[] {
  return normalizeEmailList(value).map((email) => formatEmailWithBreakOpportunities(email));
}

/**
 * Joins fragment trees (e.g. from {@link formatEmailsToDisplayFragments}) with line breaks.
 */
export function joinDisplayFragmentsWithLineBreaks(fragments: readonly React.ReactNode[]): React.ReactNode {
  if (fragments.length === 0) {
    return '';
  }
  if (fragments.length === 1) {
    return fragments[0];
  }
  return (
    <>
      {fragments.map((fragment, index) => (
        <React.Fragment key={index}>
          {index > 0 ? <br /> : null}
          {fragment}
        </React.Fragment>
      ))}
    </>
  );
}

/**
 * **Preferred entry point for showing user or account emails in the UI** when the layout is
 * width-constrained (tooltips, table cells, chips, modals) and plain strings would wrap badly
 * mid-address.
 *
 * Pipeline: {@link normalizeEmailList} → {@link formatEmailWithBreakOpportunities} per address
 * → {@link joinDisplayFragmentsWithLineBreaks}. Inserts `<wbr />` after `@` and after dots
 * between segments so line breaks land on natural boundaries; multiple addresses are separated
 * by `<br />`.
 *
 * **Input:** `string` (one address or comma-separated list), `readonly string[]`, or
 * nullish/empty (renders as `''`). Prefer this over hand-rolling `row.email` in JSX when
 * emails may be a list or a comma-separated string from the API.
 *
 * **When not to use:** Simple full-width read-only text where default wrapping is acceptable;
 * use raw strings. If you only need per-address trees (e.g. custom separators), use
 * {@link formatEmailsToDisplayFragments} and {@link joinDisplayFragmentsWithLineBreaks} instead.
 *
 * Tooltips: pass the return value to the `label` prop of `CustomTooltip`
 * (`Components/atoms/Tooltip`) and use the `custom-tooltip--email` class on the overlay so
 * width and `word-break` match the `<wbr />` markup this function emits.
 */
export function formatEmailsForDisplay(value: EmailDisplayInput): React.ReactNode {
  return joinDisplayFragmentsWithLineBreaks(formatEmailsToDisplayFragments(value));
}

function joinSegmentsWithWbr(part: string, separator: string): React.ReactNode {
  if (!part.includes(separator)) {
    return part;
  }
  const segments = part.split(separator);
  return segments.map((segment, index) => (
    <React.Fragment key={index}>
      {segment}
      {index < segments.length - 1 ? (
        <>
          {separator}
          <wbr />
        </>
      ) : null}
    </React.Fragment>
  ));
}
